import { graphqlClient } from '@/lib/graphql-client';
import { assertMutationSaved, MutationResult, MutationSaveError } from '@/lib/mutation-result';
import { buildGetHolidaysQuery } from '../graphql/queries';
import {
  DELETE_HOLIDAY_MUTATION,
  INSERT_HOLIDAY_MUTATION,
  UPDATE_HOLIDAY_MUTATION,
} from '../graphql/mutations';
import {
  GetHolidaysParams,
  Holiday,
  HolidayInsertInput,
  HolidaysPagingResult,
  HolidayUpdateInput,
} from '../types/holiday.types';

export const getHolidays = async (
  params: GetHolidaysParams
): Promise<{ Holidays: HolidaysPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetHolidaysQuery(filter, pageNo, pageSize, order),
  }) as Promise<{ Holidays: HolidaysPagingResult }>;
};

// A nationwide holiday overlaps every holiday that day; two regional ones overlap when they share a canton.
export const overlapsHoliday = (input: HolidayInsertInput, existing: Holiday): boolean => {
  const existingCantons = existing.CantonIds ?? [];
  const existingIsNationwide = !existing.IsRegional || existingCantons.length === 0;
  if (input.CantonIds.length === 0 || existingIsNationwide) return true;
  return input.CantonIds.some((cantonId) => existingCantons.includes(cantonId));
};

const assertNoOverlap = async (input: HolidayInsertInput, ownItemId?: string) => {
  const day = input.StartDate.slice(0, 10);
  const next = new Date(`${day}T00:00:00.000Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  const { Holidays } = await getHolidays({
    pageNo: 1,
    pageSize: 100,
    filter: { StartDate: { $gte: `${day}T00:00:00.000Z`, $lt: next.toISOString() } },
  });
  const clash = Holidays.items.some(
    (holiday) => holiday.ItemId !== ownItemId && overlapsHoliday(input, holiday)
  );
  if (clash) throw new MutationSaveError('HOLIDAY_SAME_DAY_EXISTS');
};

export const createHoliday = async (input: HolidayInsertInput) => {
  await assertNoOverlap(input);
  const result = (await graphqlClient.mutate({
    query: INSERT_HOLIDAY_MUTATION,
    variables: { input },
  })) as { insertHoliday?: MutationResult };
  assertMutationSaved(result.insertHoliday, true, 'HOLIDAY_SAVE_FAILED');
  return result;
};

export const updateHoliday = async (params: { itemId: string; input: HolidayUpdateInput }) => {
  await assertNoOverlap(params.input, params.itemId);
  const filter = JSON.stringify({ _id: params.itemId });
  const result = (await graphqlClient.mutate({
    query: UPDATE_HOLIDAY_MUTATION,
    variables: { filter, input: params.input },
  })) as { updateHoliday?: MutationResult };
  assertMutationSaved(result.updateHoliday, false, 'HOLIDAY_SAVE_FAILED');
  return result;
};

export const deleteHoliday = async (itemId: string) => {
  const filter = JSON.stringify({ _id: itemId });
  const result = (await graphqlClient.mutate({
    query: DELETE_HOLIDAY_MUTATION,
    variables: { filter, input: { isHardDelete: false } },
  })) as { deleteHoliday?: MutationResult };
  assertMutationSaved(result.deleteHoliday, false, 'HOLIDAY_SAVE_FAILED');
  return result;
};
